// The expanded group's lines: read on mount, again on each submitted search and on `retry`. The
// search is applied by the server (an in-memory match there); typing alone reads nothing, so a
// read is one per submit, never per keystroke. A newer read makes an older answer stale.

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchRbpGroupLines, type RbpSubGroupLines } from '../api/rbp.client';

export type RbpGroupLinesState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly subGroups: readonly RbpSubGroupLines[] };

export interface RbpGroupLines {
  readonly state: RbpGroupLinesState;
  /** The search the shown lines answer; empty lists every line. */
  readonly search: string;
  readonly applySearch: (search: string) => void;
  readonly retry: () => void;
}

export function useRbpGroupLines(benefitGroupCode: string): RbpGroupLines {
  const [state, setState] = useState<RbpGroupLinesState>({ status: 'loading' });
  const [search, setSearch] = useState('');
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    const request = ++latest.current;
    setState({ status: 'loading' });
    void fetchRbpGroupLines(benefitGroupCode, search === '' ? null : search)
      .then((subGroups) => {
        if (request === latest.current) setState({ status: 'ready', subGroups });
      })
      .catch(() => {
        if (request === latest.current) setState({ status: 'error' });
      });
    return () => {
      latest.current += 1;
    };
  }, [benefitGroupCode, search, attempt]);

  const applySearch = useCallback((next: string) => {
    setSearch(next.trim() === '' ? '' : next);
    // A repeated submit of the same text reads again, as SL1.0's form does.
    setAttempt((count) => count + 1);
  }, []);
  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  return { state, search, applySearch, retry };
}
